<template>
  <div>
    <div class="page-header">
      <div>
        <button class="back-btn" @click="$router.back()">
          <font-awesome-icon icon="arrow-left" class="me-1" /> Back
        </button>
        <h5 class="page-title">{{ isEdit ? 'Edit Sourcing Event' : 'New Sourcing Event' }}</h5>
        <p class="page-sub" v-if="isEdit && event">{{ event.event_number }}</p>
      </div>
    </div>

    <div v-if="loading" class="loading-state"><div class="spinner"></div><p>Loading...</p></div>

    <div v-else class="form-layout">
      <!-- Step tabs -->
      <div class="step-tabs">
        <button :class="['step-tab', step === 0 ? 'active' : '']" @click="step = 0">
          <span class="step-num">1</span> Event Details
        </button>
        <button :class="['step-tab', step === 1 ? 'active' : '']" @click="step = 1">
          <span class="step-num">2</span> Line Items
        </button>
      </div>

      <!-- Step 0: Event Details -->
      <div v-show="step === 0" class="form-card">
        <!-- PR Link -->
        <div class="section-head">Source</div>
        <div class="field-row" v-if="!prLocked">
          <div class="field">
            <label class="field-label">Link to Purchase Requisition <span class="opt">(optional)</span></label>
            <select v-model="form.pr_id" class="field-input" @change="onPrChange">
              <option value="">— Standalone event —</option>
              <option v-for="pr in approvedPRs" :key="pr.id" :value="pr.id">{{ pr.pr_number }} — {{ pr.title }}</option>
            </select>
          </div>
        </div>
        <div class="field-row" v-else>
          <div class="field">
            <label class="field-label">Purchase Requisition</label>
            <div class="pr-locked-badge">
              <font-awesome-icon icon="link" class="me-1" />
              {{ linkedPrLabel }}
            </div>
          </div>
        </div>

        <div class="section-head">Event Information</div>
        <div class="field-row two-col">
          <div class="field">
            <label class="field-label">Title <span class="req">*</span></label>
            <input v-model="form.title" class="field-input" placeholder="e.g. Supply of Office Stationery Q4 2026" />
          </div>
          <div class="field">
            <label class="field-label">Event Type</label>
            <select v-model="form.event_type" class="field-input">
              <option value="rfq">RFQ — Request for Quotation</option>
              <option value="rfp">RFP — Request for Proposal</option>
              <option value="rfi">RFI — Request for Information</option>
              <option value="eoi">EOI — Expression of Interest</option>
            </select>
          </div>
        </div>
        <div class="field-row two-col">
          <div class="field">
            <label class="field-label">Submission Deadline</label>
            <input v-model="form.submission_deadline" type="datetime-local" class="field-input" />
          </div>
          <div class="field">
            <label class="field-label">Valid Until</label>
            <input v-model="form.valid_until" type="date" class="field-input" />
          </div>
        </div>
        <div class="field-row two-col">
          <div class="field">
            <label class="field-label">Currency</label>
            <select v-model="form.currency" class="field-input">
              <option value="NGN">NGN — Nigerian Naira</option>
              <option value="GBP">GBP — British Pound</option>
              <option value="USD">USD — US Dollar</option>
              <option value="EUR">EUR — Euro</option>
            </select>
          </div>
          <div class="field">
            <label class="field-label">Payment Terms</label>
            <select v-model="form.payment_terms" class="field-input">
              <option value="">-- Select payment terms --</option>
              <option v-for="t in paymentTerms" :key="t.id" :value="t.name">{{ t.name }}</option>
            </select>
          </div>
        </div>
        <div class="field-row">
          <div class="field">
            <label class="field-label">Delivery Address</label>
            <input v-model="form.delivery_address" class="field-input" placeholder="Delivery location" />
          </div>
        </div>
        <div class="field-row">
          <div class="field">
            <label class="field-label">Instructions for Vendors</label>
            <textarea v-model="form.instructions" class="field-input" rows="4" placeholder="Provide instructions on how vendors should respond, what to include, format requirements, etc."></textarea>
          </div>
        </div>
        <div class="field-row">
          <div class="field">
            <label class="field-label">Internal Notes</label>
            <textarea v-model="form.notes" class="field-input" rows="2" placeholder="Internal notes (not visible to vendors)"></textarea>
          </div>
        </div>

        <div class="step-nav">
          <div></div>
          <button class="btn-primary" @click="goToLines">Next: Line Items <font-awesome-icon icon="arrow-right" class="ms-1" /></button>
        </div>
      </div>

      <!-- Step 1: Line Items -->
      <div v-show="step === 1" class="form-card">
        <div class="section-head">
          Line Items
          <button class="btn-sm-add" @click="addLine"><font-awesome-icon icon="plus" /> Add Line</button>
        </div>

        <div v-if="!form.lines.length" class="empty-lines">
          No line items yet. Click "Add Line" to begin.
        </div>

        <div v-for="(line, i) in form.lines" :key="i" class="line-block">
          <div class="line-header">
            <span class="line-num">Line {{ i + 1 }}</span>
            <button class="btn-icon btn-icon--danger" @click="removeLine(i)" title="Remove line"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="field-row two-col" v-if="prItemType === 'goods'">
            <div class="field">
              <label class="field-label">Product <span class="opt">(optional)</span></label>
              <Multiselect
                v-model="line.product_id"
                label="name"
                :options="productOptions"
                :searchable="true"
                :close-on-select="true"
                :create-option="false"
                :hideSelected="false"
                placeholder="Search product..."
                class="se-product-multiselect"
                @change="val => onProductChange(line, val)"
              />
            </div>
            <div class="field">
              <label class="field-label">Destination Location <span class="opt">(optional)</span></label>
              <Multiselect
                v-model="line.location_id"
                label="name"
                :options="locationOptions"
                :searchable="true"
                :close-on-select="true"
                :create-option="false"
                placeholder="Search location..."
                class="se-product-multiselect"
              />
            </div>
          </div>
          <div class="field-row two-col">
            <div class="field">
              <label class="field-label">Description <span class="req">*</span></label>
              <input v-model="line.description" class="field-input" placeholder="Item description" />
            </div>
            <div class="field">
              <label class="field-label">Category</label>
              <select v-model="line.category_id" class="field-input">
                <option value="">— None —</option>
                <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
              </select>
            </div>
          </div>
          <div class="field-row three-col">
            <div class="field">
              <label class="field-label">Quantity <span class="req">*</span></label>
              <input v-model.number="line.quantity" type="number" min="0" step="any" class="field-input" />
            </div>
            <div class="field">
              <label class="field-label">UOM</label>
              <input v-model="line.uom" class="field-input" list="uom-list" placeholder="e.g. pcs, kg" />
              <datalist id="uom-list">
                <option v-for="u in uoms" :key="u.id" :value="u.name" />
              </datalist>
            </div>
            <div class="field">
              <label class="field-label">Target Unit Cost <span class="opt">(internal)</span></label>
              <input v-model.number="line.target_unit_cost" type="number" min="0" step="any" class="field-input" placeholder="Budget reference" />
            </div>
          </div>
          <div class="field-row">
            <div class="field">
              <label class="field-label">Specification / Technical Notes</label>
              <textarea v-model="line.specification" class="field-input" rows="2" placeholder="Technical specs, standards, brands, etc."></textarea>
            </div>
          </div>
          <div class="field-row">
            <div class="field">
              <label class="field-label">Assign Vendors <span class="opt">(optional)</span></label>
              <Multiselect
                v-model="line.vendor_ids"
                mode="tags"
                label="label"
                value-prop="value"
                :options="vendorOptions"
                :searchable="true"
                :close-on-select="false"
                :hide-selected="false"
                placeholder="Select vendors to invite for this item..."
                class="se-vendor-multiselect"
              />
            </div>
          </div>
        </div>

        <div class="step-nav">
          <button class="btn-outline" @click="step = 0"><font-awesome-icon icon="arrow-left" class="me-1" /> Back</button>
          <div class="step-nav-actions">
            <button class="btn-outline" :disabled="saving" @click="save(false)">
              {{ isEdit ? 'Update Event' : 'Save Draft' }}
            </button>
            <button v-if="!isEdit" class="btn-primary" :disabled="saving" @click="save(true)">
              {{ saving ? 'Saving...' : 'Save & Issue' }}
            </button>
            <button v-else class="btn-primary" :disabled="saving" @click="save(false)">
              {{ saving ? 'Saving...' : 'Update Event' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import Multiselect from '@vueform/multiselect';
import '@vueform/multiselect/themes/default.css';

export default {
  name: 'SourcingEventForm',
  components: { Multiselect },
  setup() { return { toast: useToast() }; },
  data() {
    return {
      step: 0,
      loading: false,
      saving: false,
      event: null,
      approvedPRs: [],
      categories: [],
      uoms: [],
      paymentTerms: [],
      products: [],
      locations: [],
      vendors: [],
      prLocked: false,
      fromPrId: null,
      prItemType: 'goods',
      form: {
        pr_id: '',
        title: '',
        event_type: 'rfq',
        currency: 'NGN',
        submission_deadline: '',
        valid_until: '',
        payment_terms: '',
        delivery_address: '',
        instructions: '',
        notes: '',
        lines: [],
      },
    };
  },
  computed: {
    isEdit() { return !!this.$route.params.id; },
    productOptions() {
      return this.products.map(p => ({ value: p.id, name: p.product_name, uom: p.unit_of_measurement }));
    },
    locationOptions() {
      return this.locations.map(l => ({ value: l.id, name: l.store_name }));
    },
    vendorOptions() {
      return this.vendors.map(v => ({ value: v.id, label: v.name + (v.vendor_number ? ' (' + v.vendor_number + ')' : '') }));
    },
    linkedPrLabel() {
      const pr = this.approvedPRs.find(p => p.id === this.form.pr_id);
      if (pr) return pr.pr_number + ' — ' + pr.title;
      return this.form.pr_id || '—';
    },
  },
  async mounted() {
    this.fromPrId = this.$route.query.pr_id || null;
    this.loading = true;
    try {
      await Promise.all([
        this.loadApprovedPRs(),
        this.loadCategories(),
        this.loadUoms(),
        this.loadPaymentTerms(),
        this.loadProducts(),
        this.loadLocations(),
        this.loadVendors(),
      ]);
      if (this.isEdit) {
        await this.loadEvent();
      } else if (this.fromPrId) {
        this.prLocked = true;
        this.form.pr_id = this.fromPrId;
        await this.prefillFromPr(this.fromPrId);
      }
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async loadVendors() {
      try {
        const { data } = await axios.get('/procurement/vendors', { params: { per_page: 500 } });
        this.vendors = data.data?.data || [];
      } catch (e) { /* non-critical */ }
    },
    async prefillFromPr(prId) {
      try {
        const { data } = await axios.get(`/procurement/purchase-requisitions/${prId}`);
        const pr = data.data;
        this.prItemType = pr.item_type ?? 'goods';
        if (!this.form.title) this.form.title = pr.title;
        if (pr.location) {
          const parts = [pr.location.store_name, pr.location.address].filter(Boolean);
          if (!this.form.delivery_address) this.form.delivery_address = parts.join(', ');
        }
        if (pr.lines && pr.lines.length) {
          this.form.lines = pr.lines.map(l => ({
            pr_line_id:       l.id,
            product_id:       l.procurement_product_id || '',
            location_id:      pr.location_id || '',
            category_id:      l.category_id || '',
            description:      l.description,
            specification:    l.specification || '',
            quantity:         l.quantity,
            uom:              l.uom || '',
            target_unit_cost: l.estimated_unit_cost || '',
            vendor_ids:       [],
          }));
        }
      } catch (e) { /* non-critical */ }
    },
    async loadApprovedPRs() {
      try {
        const { data } = await axios.get('/procurement/purchase-requisitions', { params: { status: 'approved', per_page: 200 } });
        this.approvedPRs = data.data.data || [];
      } catch (e) { /* non-critical */ }
    },
    async loadCategories() {
      try {
        const { data } = await axios.get('/procurement/categories');
        this.categories = data.data || [];
      } catch (e) { /* non-critical */ }
    },
    async loadUoms() {
      try {
        const { data } = await axios.get('/settings/unit-of-measurement');
        this.uoms = data.data || [];
      } catch (e) { /* non-critical */ }
    },
    async loadPaymentTerms() {
      try {
        const { data } = await axios.get('/procurement/payment-terms');
        this.paymentTerms = data.data || [];
      } catch (e) { /* non-critical */ }
    },
    async loadProducts() {
      try {
        const { data } = await axios.get('/inventory/product/active/all');
        this.products = data.data ?? [];
      } catch (e) { /* non-critical */ }
    },
    async loadLocations() {
      try {
        const { data } = await axios.get('/admin/store/all');
        this.locations = data.data ?? [];
      } catch (e) { /* non-critical */ }
    },
    async loadEvent() {
      const { data } = await axios.get(`/procurement/sourcing-events/${this.$route.params.id}`);
      this.event = data.data;
      const ev = data.data;
      this.form = {
        pr_id:               ev.pr_id || '',
        title:               ev.title,
        event_type:          ev.event_type,
        currency:            ev.currency,
        submission_deadline: ev.submission_deadline ? ev.submission_deadline.slice(0, 16) : '',
        valid_until:         ev.valid_until || '',
        payment_terms:       ev.payment_terms || '',
        delivery_address:    ev.delivery_address || '',
        instructions:        ev.instructions || '',
        notes:               ev.notes || '',
        lines:               (ev.lines || []).map(l => ({
          pr_line_id:       l.pr_line_id || null,
          product_id:       l.product_id || '',
          location_id:      l.location_id || '',
          category_id:      l.category_id || '',
          description:      l.description,
          specification:    l.specification || '',
          quantity:         l.quantity,
          uom:              l.uom || '',
          target_unit_cost: l.target_unit_cost || '',
          vendor_ids:       [],
        })),
      };
    },
    onPrChange() {
      const pr = this.approvedPRs.find(p => p.id === this.form.pr_id);
      if (!pr) return;
      if (!this.form.title) this.form.title = pr.title;
      if (pr.lines && pr.lines.length) {
        this.form.lines = pr.lines.map(l => ({
          pr_line_id:       l.id,
          product_id:       l.procurement_product_id || '',
          location_id:      pr.location_id || '',
          category_id:      l.category_id || '',
          description:      l.description,
          specification:    l.specification || '',
          quantity:         l.quantity,
          uom:              l.uom || '',
          target_unit_cost: l.estimated_unit_cost || '',
          vendor_ids:       [],
        }));
      }
    },
    onProductChange(line, val) {
      const product = this.productOptions.find(p => p.value === (val ?? line.product_id));
      if (product) {
        line.description = product.name;
        if (!line.uom && product.uom) line.uom = product.uom;
      }
    },
    addLine() {
      this.form.lines.push({
        pr_line_id: null, product_id: '', location_id: '', category_id: '',
        description: '', specification: '', quantity: 1, uom: '', target_unit_cost: '',
        vendor_ids: [],
      });
    },
    removeLine(i) { this.form.lines.splice(i, 1); },
    goToLines() {
      if (!this.form.title.trim()) {
        this.toast.error('Title is required');
        return;
      }
      this.step = 1;
    },
    async save(andIssue) {
      if (!this.form.title.trim()) { this.toast.error('Title is required'); this.step = 0; return; }
      for (const line of this.form.lines) {
        if (!line.description.trim()) { this.toast.error('All line items require a description'); return; }
        if (!line.quantity || line.quantity <= 0) { this.toast.error('All line items require a valid quantity'); return; }
      }

      this.saving = true;
      try {
        // Strip client-only vendor_ids before sending to backend
        const lines = this.form.lines.map(l => { const line = { ...l }; delete line.vendor_ids; return line; });
        const payload = { ...this.form, pr_id: this.form.pr_id || null, lines };

        let eventId;
        if (this.isEdit) {
          const { data } = await axios.put(`/procurement/sourcing-events/${this.$route.params.id}`, payload);
          eventId = data.data.id;
          this.toast.success('Sourcing event updated');
        } else {
          const { data } = await axios.post('/procurement/sourcing-events', payload);
          eventId = data.data.id;
          this.toast.success('Sourcing event created');

          // Build vendor → assigned line IDs map using the server-returned line IDs
          const createdLines = data.data.lines || [];
          const vendorLineMap = {};
          this.form.lines.forEach((line, i) => {
            const createdLine = createdLines[i];
            if (!createdLine) return;
            (line.vendor_ids || []).forEach(vendorId => {
              if (!vendorLineMap[vendorId]) vendorLineMap[vendorId] = [];
              vendorLineMap[vendorId].push(createdLine.id);
            });
          });
          // Invite each vendor with their specific line assignments
          for (const [vendorId, lineIds] of Object.entries(vendorLineMap)) {
            try {
              await axios.post(`/procurement/sourcing-events/${eventId}/vendors`, {
                vendor_id: vendorId,
                assigned_line_ids: lineIds,
              });
            } catch (e) { /* non-fatal */ }
          }

          if (andIssue) {
            await axios.post(`/procurement/sourcing-events/${eventId}/issue`);
            this.toast.success('RFQ issued to vendors');
          }
        }

        this.$router.push(`/procurement/sourcing-events/${eventId}`);
      } catch (e) {
        this.toast.error(e.response?.data?.message || 'Failed to save event');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.page-header { margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:6px 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.back-btn    { display:inline-flex; align-items:center; gap:6px; background:transparent; border:none; color:#7a90b0; font-size:13px; cursor:pointer; padding:0; margin-bottom:4px; }
.back-btn:hover { color:#004AAD; }
.me-1 { margin-right:4px; }
.ms-1 { margin-left:4px; }

.loading-state { display:flex; flex-direction:column; align-items:center; padding:60px 24px; gap:10px; color:#7a90b0; }
.spinner { width:32px; height:32px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }

.form-layout { max-width:900px; }

.step-tabs { display:flex; gap:8px; margin-bottom:16px; }
.step-tab  { display:inline-flex; align-items:center; gap:8px; padding:10px 20px; border-radius:9px; border:1.5px solid #dde5f0; background:#fff; font-size:13px; font-weight:600; color:#7a90b0; cursor:pointer; transition:all .15s; }
.step-tab.active { background:#004AAD; color:#fff; border-color:#004AAD; }
.step-num  { width:22px; height:22px; border-radius:50%; background:rgba(255,255,255,.25); display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; }
.step-tab:not(.active) .step-num { background:#f1f5f9; color:#475569; }

.form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:28px; }
.section-head { font-size:13px; font-weight:700; color:#7a90b0; text-transform:uppercase; letter-spacing:.06em; margin:0 0 16px; padding-bottom:8px; border-bottom:1px solid #f0f4f8; display:flex; align-items:center; justify-content:space-between; }
.section-head:not(:first-child) { margin-top:28px; }

.field-row { display:flex; gap:16px; margin-bottom:14px; flex-wrap:wrap; }
.field-row.two-col > .field { flex:1; min-width:220px; }
.field-row.three-col > .field { flex:1; min-width:140px; }
.field-row > .field:not([class*="col"]) { flex:1; min-width:100%; }
.field { display:flex; flex-direction:column; gap:5px; }
.field-label { font-size:12.5px; font-weight:600; color:#374151; }
.field-input { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; outline:none; background:#fff; width:100%; box-sizing:border-box; resize:vertical; }
.field-input:focus { border-color:#004AAD; }
.req { color:#dc2626; }
.opt { color:#aab8cc; font-weight:400; }

.line-block { border:1px solid #e4eaf2; border-radius:10px; padding:18px; margin-bottom:14px; background:#fafbfc; }
.line-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
.line-num    { font-size:13px; font-weight:700; color:#004AAD; }
.empty-lines { text-align:center; padding:40px 24px; color:#aab8cc; font-size:14px; border:2px dashed #e4eaf2; border-radius:10px; margin-bottom:16px; }

.btn-sm-add { display:inline-flex; align-items:center; gap:5px; background:#eff6ff; color:#004AAD; border:1.5px solid #bfdbfe; border-radius:7px; padding:5px 12px; font-size:12px; font-weight:600; cursor:pointer; }
.btn-sm-add:hover { background:#dbeafe; }

.step-nav { display:flex; align-items:center; justify-content:space-between; margin-top:24px; padding-top:20px; border-top:1px solid #f0f4f8; }
.step-nav-actions { display:flex; gap:10px; }
.btn-icon { width:30px; height:30px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; color:#1a2b45; display:flex; align-items:center; justify-content:justify; font-size:13px; cursor:pointer; }
.btn-icon--danger:hover { background:#fee2e2; border-color:#fecaca; color:#dc2626; }

.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary:hover { background:#003b8e; }
.btn-primary:disabled { opacity:.5; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-outline:hover:not(:disabled) { background:#f4f7fb; }
.btn-outline:disabled { opacity:.5; cursor:not-allowed; }
.se-product-multiselect { --ms-font-size:13px; --ms-line-height:1.4; --ms-min-height:38px; --ms-border-color:#dde5f0; --ms-border-radius:8px; --ms-ring-color:rgba(0,74,173,.08); --ms-dropdown-max-height:220px; }
.se-vendor-multiselect  { --ms-font-size:13px; --ms-line-height:1.4; --ms-min-height:38px; --ms-border-color:#dde5f0; --ms-border-radius:8px; --ms-ring-color:rgba(109,40,217,.08); --ms-dropdown-max-height:220px; --ms-tag-bg:#ede9fe; --ms-tag-color:#5b21b6; --ms-tag-font-size:12px; }
.pr-locked-badge { display:inline-flex; align-items:center; gap:6px; background:#eff6ff; color:#1d4ed8; border:1px solid #bfdbfe; border-radius:8px; padding:8px 14px; font-size:13px; font-weight:600; }
</style>
